import { useEffect, useState } from "react";
import api from "../lib/api";
import { TREATMENTS } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Tags, Pencil, Check } from "lucide-react";

const PALETTE = ["#113E35", "#0284C7", "#059669", "#D97706", "#E11D48", "#7C3AED",
  "#0891B2", "#CA8A04", "#DB2777", "#65A30D", "#4F46E5", "#EA580C"];
const emptyForm = { name: "", type: "expense", treatment: "standard", color: PALETTE[0] };

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type, treatment: c.treatment, color: c.color || PALETTE[0] }); setOpen(true); };

  const save = async () => {
    if (!form.name) return;
    if (editing) {
      await api.put(`/categories/${editing.id}`, { name: form.name, treatment: form.treatment, color: form.color });
      toast.success("Category updated");
    } else {
      await api.post("/categories", form);
      toast.success("Category added");
    }
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    load();
  };
  const remove = async (id) => { await api.delete(`/categories/${id}`); toast.success("Removed"); load(); };

  const groups = [
    { key: "income", label: "Income categories" },
    { key: "expense", label: "Expense categories" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">GST Categories</h1>
          <p className="text-muted-foreground text-sm mt-1">Pre-configured ATO categories plus your own. Rename, recolour or change the GST treatment of any heading.</p>
        </div>
        <Button onClick={openAdd} data-testid="add-category-btn" className="gap-2"><Plus className="w-4 h-4" /> Add category</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {groups.map((g) => (
          <div key={g.key} className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm flex items-center gap-2">
              <Tags className="w-4 h-4" /> {g.label}
            </div>
            <div className="divide-y divide-border">
              {cats.filter((c) => c.type === g.key).map((c) => {
                const tr = TREATMENTS[c.treatment];
                return (
                  <div key={c.id} className="flex items-center justify-between px-4 py-3 row-hover" data-testid={`category-${c.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-3.5 h-3.5 rounded-full shrink-0 ring-1 ring-black/5" style={{ background: c.color || "#94A3B8" }} data-testid={`category-color-${c.id}`} />
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-md border font-medium shrink-0 ${tr.color}`}>{tr.label}</span>
                      {!c.custom && <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono shrink-0">ATO</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}
                        className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {c.custom && (
                        <button onClick={() => remove(c.id)} data-testid={`delete-category-${c.id}`}
                          className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit category" : "New category"}</DialogTitle>
            <DialogDescription>Set the name, GST treatment and colour used across reports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="category-name" placeholder="e.g. Subcontractors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1.5" data-testid="category-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST treatment</Label>
                <Select value={form.treatment} onValueChange={(v) => setForm({ ...form, treatment: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="category-treatment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TREATMENTS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Colour</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="category-color-picker">
                {PALETTE.map((col) => (
                  <button key={col} type="button" onClick={() => setForm({ ...form, color: col })}
                    data-testid={`color-swatch-${col.replace("#", "")}`}
                    className="w-7 h-7 rounded-full grid place-items-center ring-2 transition-transform hover:scale-110"
                    style={{ background: col, ...(form.color === col ? { boxShadow: `0 0 0 2px white, 0 0 0 4px ${col}` } : {}) }}>
                    {form.color === col && <Check className="w-3.5 h-3.5 text-white" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-category-btn">{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
